// TL reviews showcase.
class ReviewsShowcase extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.observer = null;
    this.slideObserver = null;
    this.loaded = false;
  }

  // locale strings with fallback
  text(key, fallback) {
    return this.dataset[key] || fallback;
  }

  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.slider = this.querySelector('embla-slider');
    this.container = this.querySelector('embla-slider-container');
    this.stars = this.querySelector('[data-review-stars]');
    this.ratingText = this.querySelector('[data-review-rating]');
    document.addEventListener('shopify:section:load', this.onSectionLoad, {
      signal: this.abortController.signal
    });

    this.observe();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.observer?.disconnect();
    this.slideObserver?.disconnect();
    this.abortController = null;
    this.observer = null;
    this.slideObserver = null;
    this.loaded = false;
  }

  onSectionLoad = (event) => {
    if (!event.target?.contains?.(this)) return;
    this.disconnectedCallback();
    this.connectedCallback();
  };

  get token() {
    return this.dataset.judgeMeToken;
  }

  get shopDomain() {
    return this.dataset.shopDomain;
  }

  get perPage() {
    return parseInt(this.dataset.perPage || '20', 10);
  }

  get designMode() {
    return this.dataset.designMode === 'true';
  }

  observe() {
    if (!('IntersectionObserver' in window)) {
      this.load();
      return;
    }

    const target = this.closest('.tl-reviews-showcase') || this.querySelector('.tl-reviews-showcase__carousel') || this;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.observer?.disconnect();
        this.observer = null;
        this.load();
      },
      { rootMargin: '1000px 0px' }
    );

    this.observer.observe(target);
  }

  load() {
    if (this.loaded) return;

    this.loaded = true;
    this.fetchStats();
    this.fetchReviews();
  }

  async fetchStats() {
    if (!this.token || !this.shopDomain || !this.stars || !this.ratingText) {
      if (this.ratingText) this.ratingText.textContent = '';
      return;
    }

    const params = new URLSearchParams({
      api_token: this.token,
      shop_domain: this.shopDomain,
    });

    try {
      const ratingSignal = AbortSignal.any && AbortSignal.timeout
        ? AbortSignal.any([this.abortController.signal, AbortSignal.timeout(8000)])
        : this.abortController.signal;
      const [countResponse, ratingResponse] = await Promise.all([
        fetch(`https://judge.me/api/v1/widgets/all_reviews_count?${params}`, {
          signal: ratingSignal,
        }),
        fetch(`https://judge.me/api/v1/widgets/all_reviews_rating?${params}`, {
          signal: ratingSignal,
        }),
      ]);

      if (!countResponse.ok || !ratingResponse.ok) {
        this.ratingText.textContent = '';
        return;
      }

      const [countData, ratingData] = await Promise.all([countResponse.json(), ratingResponse.json()]);
      const count = countData.count || countData.all_reviews_count || 0;
      const rating = parseFloat(ratingData.rating || ratingData.all_reviews_rating || 0);

      if (!rating) {
        this.ratingText.textContent = '';
        return;
      }

      this.replaceStars(this.stars, rating);
      this.ratingText.textContent = `${rating.toFixed(2)} (${Number(count).toLocaleString()})`;
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.ratingText.textContent = '';
      }
    }
  }

  async fetchReviews() {
    if (!this.token || !this.shopDomain) {
      this.renderMessage(
        this.designMode
          ? this.text('textDesignMode', 'Add Judge.me settings to load reviews.')
          : this.text('textUnavailable', 'Reviews are currently unavailable.')
      );
      return;
    }

    const params = new URLSearchParams({
      api_token: this.token,
      shop_domain: this.shopDomain,
      per_page: `${this.perPage}`,
      json_request: 'true',
    });

    try {
      const reviewsSignal = AbortSignal.any && AbortSignal.timeout
        ? AbortSignal.any([this.abortController.signal, AbortSignal.timeout(8000)])
        : this.abortController.signal;
      const response = await fetch(`https://judge.me/api/v1/widgets/all_reviews_page?${params}`, {
        signal: reviewsSignal,
      });
      if (!response.ok) throw new Error('Reviews request failed.');

      const data = await response.json();
      const reviews = Array.isArray(data.reviews) ? data.reviews : [];

      if (!reviews.length) {
        this.renderMessage(this.text('textEmpty', 'No reviews found.'));
        return;
      }

      this.renderReviews(
        reviews.map((review) => ({
          body: review.body || '',
          rating: review.rating || 5,
          name: review.reviewer_name || this.text('textAnonymous', 'Anonymous'),
          verified: Boolean(review.verified_buyer),
          productTitle: review.product_title || '',
          productHandle: this.getProductHandle(review.product_url),
        }))
      );
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.renderMessage(this.text('textError', 'Failed to load reviews.'));
      }
    }
  }

  renderReviews(reviews) {
    if (!this.container) return;

    const slides = reviews.map((review, index) => this.createSlide(this.createReviewCard(review), index));
    this.container.replaceChildren(...slides);
    this.syncInert();
    this.resetSlider(slides.length);
  }

  renderMessage(message) {
    if (!this.container) return;

    const item = this.createElement('div', 'resource-list__item');
    const loading = this.createElement('div', 'tl-reviews-showcase__loading');
    loading.textContent = message;
    item.append(loading);
    this.container.replaceChildren(this.createSlide(item, 0));
    this.syncInert();
    this.resetSlider(1);
  }

  // mirror inert onto aria-hidden
  syncInert() {
    if (!this.container) return;

    const apply = (slide) => {
      if (slide.tagName === 'EMBLA-SLIDER-SLIDE') {
        slide.inert = slide.getAttribute('aria-hidden') === 'true';
      }
    };

    Array.from(this.container.children).forEach(apply);

    this.slideObserver?.disconnect();
    this.slideObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => apply(mutation.target));
    });
    this.slideObserver.observe(this.container, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true,
    });
  }

  createSlide(child, index) {
    const slide = document.createElement('embla-slider-slide');
    slide.className = 'resource-list__slide tl-reviews-showcase__slide embla__slide';
    slide.setAttribute('ref', 'slides[]');
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    slide.style.setProperty('--slider-timeline', `--slide-${index}`);
    slide.append(child);

    return slide;
  }

  createReviewCard(review) {
    const item = this.createElement('div', 'resource-list__item');
    const article = this.createElement('article', 'tl-reviews-showcase__card');

    const text = this.createElement('p', 'tl-reviews-showcase__text');
    text.textContent = review.body;

    const stars = this.createElement('div', 'tl-reviews-showcase__card-stars');
    stars.setAttribute('role', 'img');
    stars.setAttribute(
      'aria-label',
      this.text('textStarsLabel', '__RATING__ out of 5 stars').replace('__RATING__', Number(review.rating).toFixed(1))
    );
    stars.append(...this.createStars(review.rating));

    const reviewer = this.createElement('div', 'tl-reviews-showcase__reviewer');
    const name = this.createElement('span', 'tl-reviews-showcase__name');
    name.textContent = review.name;
    reviewer.append(name);

    if (review.verified) {
      reviewer.append(this.createSvg('[data-review-icon-verified]', 'tl-reviews-showcase__verified'));
    }

    article.append(text, stars, reviewer);

    if (review.productTitle) {
      const product = this.createElement('div', 'tl-reviews-showcase__product');
      product.append(this.createProductLink(review));
      article.append(product);
    }

    item.append(article);
    return item;
  }

  createProductLink(review) {
    if (!review.productHandle) {
      return document.createTextNode(review.productTitle);
    }

    const link = this.createElement('a');
    link.href = `/products/${encodeURIComponent(review.productHandle)}`;
    link.draggable = false;
    link.textContent = review.productTitle;

    return link;
  }

  replaceStars(target, rating) {
    target.replaceChildren(...this.createStars(rating));
  }

  createStars(rating) {
    const roundedRating = Math.round(Number(rating) || 0);
    const stars = [];

    for (let index = 1; index <= 5; index += 1) {
      const filledClass = index <= roundedRating ? ' is-filled' : '';
      stars.push(this.createSvg('[data-review-icon-star]', `tl-reviews-showcase__star${filledClass}`));
    }

    return stars;
  }

  createSvg(templateSelector, className) {
    const template = this.querySelector(templateSelector);
    const svg = template?.content?.firstElementChild?.cloneNode(true);

    if (!(svg instanceof SVGElement)) {
      const fallback = document.createElement('span');
      fallback.className = className;
      fallback.setAttribute('aria-hidden', 'true');
      return fallback;
    }

    svg.setAttribute('class', className);
    return svg;
  }

  getProductHandle(productUrl = '') {
    try {
      const url = new URL(productUrl, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);

      return match?.[1] || '';
    } catch {
      return '';
    }
  }

  resetSlider(slideCount) {
    if (!this.slider) return;

    const timeline = Array.from({ length: slideCount }, (_, index) => `--slide-${index}`).join(',');
    this.slider.style.setProperty('--slider-timeline', timeline);
    this.slider.setAttribute('initial-slide', '0');

    customElements.whenDefined('embla-slider').then(() => {
      requestAnimationFrame(() => this.slider?.reInit?.());
    });
  }

  createElement(tagName, className = '') {
    const element = document.createElement(tagName);
    if (className) element.className = className;

    return element;
  }
}

if (!customElements.get('tl-reviews-showcase')) {
  customElements.define('tl-reviews-showcase', ReviewsShowcase);
}
